/**
 * Macro #22 — client-side referral PENDING INTENT (§19).
 *
 * A `?ref=<opaque-code>` in the URL is ONLY a pending intent. It survives reload
 * locally, but it NEVER qualifies anything and is NEVER canonical truth. After
 * the wallet authenticates, the code is submitted to the server-authoritative
 * attribution endpoint; the SERVER decides valid / invalid / self / already
 * attributed / already owner / inactive referrer. Once the server has spoken
 * (success OR any terminal semantic), the local intent is cleared.
 */
import { parcelUtilityApi, ParcelUtilityError } from "./parcel-utility-api";

const STORAGE_KEY = "grid_ref_intent";

/** Server verdicts that mean "stop trying" — the intent is resolved. */
const TERMINAL_CODES = new Set([
  "REFERRAL_SELF",
  "REFERRAL_EXISTS",
  "REFERRAL_ALREADY_OWNER",
  "REFERRAL_CODE_UNKNOWN",
  "VALIDATION",
]);

function readParam(): string | null {
  try {
    // Support both the query string and a hash-embedded query (#dashboard?ref=…).
    const search = new URLSearchParams(window.location.search);
    let code = search.get("ref");
    if (!code && window.location.hash.includes("?")) {
      const hashQuery = window.location.hash.split("?")[1] || "";
      code = new URLSearchParams(hashQuery).get("ref");
    }
    return code ? code.trim() : null;
  } catch {
    return null;
  }
}

/** Capture a `?ref=` code as pending intent (idempotent; first capture wins). */
export function captureReferralIntent(): void {
  try {
    const code = readParam();
    if (!code) return;
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (!existing) window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* localStorage unavailable — pending intent simply not persisted */
  }
}

export function getPendingReferralIntent(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearReferralIntent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Submit any pending referral intent for the just-authenticated wallet.
 * Best-effort and safe to call repeatedly: clears the intent on success or on a
 * terminal server verdict; leaves it in place on transient/network errors.
 */
export async function flushReferralIntent(): Promise<void> {
  const code = getPendingReferralIntent();
  if (!code) return;
  try {
    await parcelUtilityApi.submitReferralCode(code);
    clearReferralIntent();
  } catch (e) {
    if (e instanceof ParcelUtilityError && TERMINAL_CODES.has(e.code)) {
      clearReferralIntent();
      return;
    }
    // Transient (401 not-yet-authenticated, network) — keep for a later retry.
  }
}
